import React, { useCallback, useRef, useState } from 'react';
import { ThemeProvider } from 'styled-components';

import { Container, StyledProgressIndicator, GlobalStyle } from './App.styles';

import Carousel from '../Carousel/Carousel';

import { useInitApplication } from '../../hooks/init';
import { useIsLargeScreen, useGetHostPlatform } from '../../hooks/utility';

import { ANDROID, IOS, triggerEvent } from 'jweb';
import { resetTiles } from '../../utils/request';
import {
  getTilesSeenFromStorage,
  setTilesSeenToStorage,
  trackLinkClick,
  trackPageViewUDLEvent,
  trackTileClickedUDLEvent,
  trackTileDismissalEvent,
  trackTileViewUDLEvent
} from '../../utils/analytics';
import {
  trackTileViewSimpleUIEvent,
  trackTileClickedSimpleUIEvent,
  trackTileDismissalSimpleUIEvent,
  trackLinkClickSimpleUIEvent
} from '../../utils/simpleUIAnalytics';
import { getPrinterInfo, getRawUDLData } from '../../utils/persistData';
import { useInitialFetch } from '../../hooks/initialFetch';
import { getQueryParams } from '../../utils/functional';
import Header from '../Header';
import eventsDispatcher from '../../utils/eventsDispatcher';

const   createHandleTileClick =
  (sessionId, locale, fullScreen, hostPlatform, handleTileInteraction, splunkData) => tile => {
    if (!tile.seen) {
      trackTileViewUDLEvent(tile);
      trackTileViewSimpleUIEvent(tile);
    }
    trackTileClickedUDLEvent(tile);
    trackTileClickedSimpleUIEvent(tile);
    if (tile.saveCompletedState) {
      sessionStorage.setItem('clickedTile', tile.engagementName);
    }

    tile.target.source = 'tile';
    if (typeof tile.segmentName !== 'undefined') {
      tile.target.segmentName = tile.segmentName;
    }
    const params = [
      tile.target,
      tile.engagementName,
      tile.jumpId,
      sessionId,
      locale,
      getRawUDLData(),
      tile.analyticsData,
      fullScreen.current,
      hostPlatform,
      JSON.stringify(splunkData)
    ];
    if (!tile.clicked) {
      handleTileInteraction(tile, 'clicked').then(() => {
        triggerEvent(...params);
      });
    } else {
      triggerEvent(...params);
    }
  };

const App = props => {
  const [inDebugMode, setInDebugMode] = useState(false);
  const isLargeScreen = useIsLargeScreen();
  const hostPlatform = useGetHostPlatform();
  const isMobile = hostPlatform === ANDROID || hostPlatform === IOS;
  const params = getQueryParams();
  const fullScreen = useRef(!!params.fullscreen);
  const { widgetData, isInitialFetching } = useInitialFetch(params);

  const { splunkData } = props;

  const {
    tiles,
    handleNativeStateChange,
    handleTileInteraction,
    disableListeners,
    isInitialLoad,
    theme
  } = useInitApplication(widgetData, fullScreen.current);

  const resetTilesState = useCallback(async () => {
    try {
      await resetTiles();
      handleNativeStateChange();
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error reseting tiles');
    }
  }, []);

  if (!widgetData) {
    return fullScreen.current ? <StyledProgressIndicator theme={theme} /> : null;
  }

  const { sessionId, locale, isResetEnabled } = widgetData;

  const handleTileClick = tile =>
    createHandleTileClick(sessionId, locale, fullScreen, hostPlatform, handleTileInteraction, splunkData)(tile);

  const tileDismissed = (tile, callback) => {
    handleTileInteraction(tile, 'dismissed').then(() => {
      callback();
    });
    trackTileDismissalEvent(tile);
    trackTileDismissalSimpleUIEvent(tile);
  };

  const tileSeen = (tiles, isOnLoad) => {
    if (tiles && tiles.length) {
      handleTileInteraction(tiles, 'seen');
      if (!isOnLoad) {
        const alreadySeenTiles = getTilesSeenFromStorage();
        const newSeenTiles = tiles.filter(tile => !alreadySeenTiles.includes(tile.engagementId));
        if (newSeenTiles.length) {
          trackTileViewUDLEvent(newSeenTiles);
          trackTileViewSimpleUIEvent(newSeenTiles);
          setTilesSeenToStorage(newSeenTiles);
        }
      } else {
        trackPageViewUDLEvent(tiles, fullScreen.current);
        trackTileViewSimpleUIEvent(tiles);
        setTilesSeenToStorage(tiles);
      }
    }
  };

  const showAllTiles = () => {
    const target = {
      type: 'showAll',
      value: `${window.location.origin}${window.location.pathname}`
    };
    disableListeners();
    triggerEvent(target, null, null, sessionId, locale, getRawUDLData(), null, hostPlatform);
    trackLinkClick('CEC', 'showAll');
    trackLinkClickSimpleUIEvent('Cec', 'ShowAll');
    sessionStorage.setItem('openedShowAll', 'true');
  };

  const handleBackClick = () => {
    disableListeners(); // Handling a windows/mac error due to multiple open jweb instances
    triggerEvent({ type: 'closeJweb' });
    trackLinkClick('header', 'close');
    trackLinkClickSimpleUIEvent('Header', 'Close');
  };

  const setDebugMode = state => {
    setInDebugMode(state);

    if (state) {
      eventsDispatcher.start();
    } else {
      eventsDispatcher.stop();
    }
  };

  let filteredTiles = tiles;
  if (!fullScreen.current && isLargeScreen) {
    filteredTiles = tiles.slice(0, 3);
  }

  const widget = isInitialFetching ? (
    <StyledProgressIndicator theme={theme} />
  ) : (
    <>
      <GlobalStyle />
      <ThemeProvider theme={theme}>
        <Container
          className={`${!tiles.length && !fullScreen.current ? 'closed' : ''} ${
            inDebugMode ? 'debug-mode' : ''
          }`}
          role='application'>
          <Header
            isMobile={isMobile}
            headerText={widgetData.widgetText}
            onReset={resetTilesState}
            onShowAll={showAllTiles}
            onBack={handleBackClick}
            theme={theme}
            isResetEnabled={isResetEnabled}
            onDebugModeToggle={setDebugMode}
            isShowAllEnabled={
              (isLargeScreen && tiles.length > 3) || (!isLargeScreen && tiles.length > 1)
            }
          />
          {Boolean(tiles.length) && (
            <Carousel
              isLargeScreen={isLargeScreen}
              tiles={filteredTiles}
              onCardClick={handleTileClick}
              onClose={tileDismissed}
              onSeen={tileSeen}
              isInitialLoad={isInitialLoad}
              theme={theme}
            />
          )}
        </Container>
      </ThemeProvider>
    </>
  );

  return widget;
};

export default App;
